import React, { useCallback, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { makeStyles } from "@material-ui/core/styles";
import Loading from "../../../../common/Loading";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "components/CustomButtons/Button";
import { Box, Divider } from "@material-ui/core";
import Close from "@material-ui/icons/Close";

const validationSchema = Yup.object().shape({
  amounts: Yup.array().of(
    Yup.object().shape({
      description: Yup.string().required("Description is required"),
      amount: Yup.number()
        .min(0, "Min value 0.")
        .required("Amount is required"),
      printing: Yup.number().min(0, "Min value 0."),
      other: Yup.number().min(0, "Min value 0."),
      pages: Yup.number().min(0, "Min value 0."),
      adPages: Yup.number().min(0, "Min value 0."),
      staffPercent: Yup.number().min(0, "Min value 0.")
    })
  )
});

const useStyles = makeStyles(theme => ({
  dialogActions: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2)
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  marginRight: {
    marginRight: theme.spacing(3)
  },
  actions: {
    margin: "0 0 0 5px",
    padding: 5,
    "& svg": {
      margin: 0
    }
  }
}));

const EditAdvertisingIncomeDialog = ({ open, data, onClose, onSave }) => {
  const classes = useStyles();

  const parsedData = data.map(item => ({
    id: item.id,
    description: item.description,
    amount: item.amount ? item.amount.toFixed(2) : 0,
    printing: item.printing ? item.printing.toFixed(2) : 0,
    other: item.other ? item.other.toFixed(2) : 0,
    pages: item.pages ? item.pages.toFixed(2) : 0,
    adPages: item.adPages ? item.adPages.toFixed(2) : 0,
    staffPercent: item.staffPercent ? item.staffPercent.toFixed(2) : 0
  }));

  const [isDirty, setDirty] = useState(false);

  const handleSubmit = useCallback(
    values => {
      setDirty(true);
      const items = values.amounts;
      const editedItems = items.filter(item =>
        parsedData.find(
          newItem =>
            newItem.id === item.id &&
            (item.description !== newItem.description ||
              item.amount !== newItem.amount ||
              item.printing !== newItem.printing ||
              item.other !== newItem.other ||
              item.pages !== newItem.pages ||
              item.adpages !== newItem.adpages ||
              item.staffPercent !== newItem.staffPercent)
        )
          ? true
          : false
      );
      const removedItems = parsedData.filter(item =>
        items.find(newItem => newItem.id === item.id) ? false : true
      );
      onSave({
        items,
        editedItems,
        removedItems
      });
    },
    [parsedData]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Chapter Financial Information</DialogTitle>
      <DialogContent>
        <Grid container direction="column">
          <Typography>
            <strong>Edit Advertising Income</strong>
          </Typography>
          <Typography className={classes.marginBottom}>
            Please fill in the dollar amount received for each of the items
            below (if applicable).
          </Typography>
          <Formik
            initialValues={{ amounts: parsedData }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              touched,
              errors,
              setValues,
              handleChange,
              handleBlur,
              isValid
            }) => (
              <Form autoComplete="off">
                {values.amounts.length === 0 && (
                  <Typography>No items to display</Typography>
                )}
                {values.amounts.map((item, index) => {
                  return (
                    <Grid item key={`formik-finance-${index}`}>
                      <Grid container spacing={2}>
                        <Grid item md={10} sm={6} xs={12}>
                          <TextField
                            label="Description"
                            variant="outlined"
                            name={`amounts[${index}].description`}
                            fullWidth
                            value={item.description}
                            onChange={handleChange}
                            error={Boolean(
                              touched.amounts &&
                                touched.amounts[index] &&
                                touched.amounts[index].description &&
                                errors.amounts &&
                                errors.amounts[index] &&
                                errors.amounts[index].description
                            )}
                            helperText={
                              touched.amounts &&
                              touched.amounts[index] &&
                              touched.amounts[index].description &&
                              errors.amounts &&
                              errors.amounts[index] &&
                              errors.amounts[index].description
                            }
                          />
                        </Grid>
                        <Grid item md={2} sm={12} xs={12}>
                          <TextField
                            fullWidth
                            label="Amount"
                            variant="outlined"
                            name={`amounts[${index}].amount`}
                            value={item.amount}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              )
                            }}
                            onChange={handleChange}
                            error={Boolean(
                              touched.amounts &&
                                touched.amounts[index] &&
                                touched.amounts[index].amount &&
                                errors.amounts &&
                                errors.amounts[index] &&
                                errors.amounts[index].amount
                            )}
                            helperText={
                              touched.amounts &&
                              touched.amounts[index] &&
                              touched.amounts[index].amount &&
                              errors.amounts &&
                              errors.amounts[index] &&
                              errors.amounts[index].amount &&
                              "Enter a valid number"
                            }
                          />
                        </Grid>
                      </Grid>
                      <Box mt={2} mb={2}>
                        <TextField
                          label="Total printing, mailing and other direct expenses to produce"
                          variant="outlined"
                          name={`amounts[${index}].printing`}
                          fullWidth
                          value={item.printing}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            )
                          }}
                          onChange={handleChange}
                          error={Boolean(
                            touched.amounts &&
                              touched.amounts[index] &&
                              touched.amounts[index].printing &&
                              errors.amounts &&
                              errors.amounts[index] &&
                              errors.amounts[index].printing
                          )}
                          helperText={
                            touched.amounts &&
                            touched.amounts[index] &&
                            touched.amounts[index].printing &&
                            errors.amounts &&
                            errors.amounts[index] &&
                            errors.amounts[index].printing &&
                            "Enter a valid number"
                          }
                        />
                      </Box>
                      <Box mb={2}>
                        <TextField
                          label="Other expenses directly related to production"
                          variant="outlined"
                          name={`amounts[${index}].other`}
                          fullWidth
                          value={item.other}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            )
                          }}
                          onChange={handleChange}
                          error={Boolean(
                            touched.amounts &&
                              touched.amounts[index] &&
                              touched.amounts[index].other &&
                              errors.amounts &&
                              errors.amounts[index] &&
                              errors.amounts[index].other
                          )}
                          helperText={
                            touched.amounts &&
                            touched.amounts[index] &&
                            touched.amounts[index].other &&
                            errors.amounts &&
                            errors.amounts[index] &&
                            errors.amounts[index].other &&
                            "Enter a valid number"
                          }
                        />
                      </Box>
                      <Box mb={2}>
                        <TextField
                          label="Total pages of publication/website"
                          variant="outlined"
                          name={`amounts[${index}].pages`}
                          fullWidth
                          value={item.pages}
                          onChange={handleChange}
                          error={Boolean(
                            touched.amounts &&
                              touched.amounts[index] &&
                              touched.amounts[index].pages &&
                              errors.amounts &&
                              errors.amounts[index] &&
                              errors.amounts[index].pages
                          )}
                          helperText={
                            touched.amounts &&
                            touched.amounts[index] &&
                            touched.amounts[index].pages &&
                            errors.amounts &&
                            errors.amounts[index] &&
                            errors.amounts[index].pages &&
                            "Enter a valid number"
                          }
                        />
                      </Box>
                      <Box mb={2}>
                        <TextField
                          label="Total number of ad pages in publication/website"
                          variant="outlined"
                          name={`amounts[${index}].adPages`}
                          fullWidth
                          value={item.adPages}
                          onChange={handleChange}
                          error={Boolean(
                            touched.amounts &&
                              touched.amounts[index] &&
                              touched.amounts[index].adPages &&
                              errors.amounts &&
                              errors.amounts[index] &&
                              errors.amounts[index].adPages
                          )}
                          helperText={
                            touched.amounts &&
                            touched.amounts[index] &&
                            touched.amounts[index].adPages &&
                            errors.amounts &&
                            errors.amounts[index] &&
                            errors.amounts[index].adPages &&
                            "Enter a valid number"
                          }
                        />
                      </Box>
                      <Box mb={2}>
                        <TextField
                          label="Percent of paid staff time spent on publication/website"
                          variant="outlined"
                          name={`amounts[${index}].staffPercent`}
                          fullWidth
                          value={item.staffPercent}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="start">
                                %
                              </InputAdornment>
                            )
                          }}
                          onChange={handleChange}
                          error={Boolean(
                            touched.amounts &&
                              touched.amounts[index] &&
                              touched.amounts[index].staffPercent &&
                              errors.amounts &&
                              errors.amounts[index] &&
                              errors.amounts[index].staffPercent
                          )}
                          helperText={
                            touched.amounts &&
                            touched.amounts[index] &&
                            touched.amounts[index].staffPercent &&
                            errors.amounts &&
                            errors.amounts[index] &&
                            errors.amounts[index].staffPercent &&
                            "Enter a valid number"
                          }
                        />
                      </Box>
                      <Box display="flex" justifyContent="flex-end">
                        <Button
                          simple
                          color="danger"
                          onClick={() => {
                            setValues({
                              amounts: values.amounts.filter(
                                newItem => newItem.id !== item.id
                              )
                            });
                          }}
                          className={classes.actions}
                        >
                          <Close />
                        </Button>
                      </Box>
                      {index < values.amounts.length - 1 && (
                        <Box mb={4} mt={4}>
                          <Divider />
                        </Box>
                      )}
                    </Grid>
                  );
                })}
                <div className={classes.dialogActions}>
                  {isDirty ? (
                    <Loading />
                  ) : (
                    <>
                      <Button
                        className={classes.marginRight}
                        color="success"
                        variant="contained"
                        type="submit"
                      >
                        Submit
                      </Button>
                      <Button
                        color="danger"
                        variant="contained"
                        onClick={onClose}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EditAdvertisingIncomeDialog;
